import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { articlesPageData, lineUrl } from "@/mocks/siteData";
import SEO from "@/components/base/SEO";

export default function Articles() {
  const [activeCategory, setActiveCategory] = useState<string>("all");

  const filtered =
    activeCategory === "all"
      ? articlesPageData.articles
      : articlesPageData.articles.filter((a) => a.category === activeCategory);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("revealed");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    document.querySelectorAll(".reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [activeCategory]);

  return (
    <>
      <SEO
        title="專欄文章｜時光逆行駭客｜曾驛翔院長逆齡觀點分享"
        description="曾驛翔院長的逆齡觀點與專業知識分享。從男性懸崖式衰老、青春痘照護、醫美儀器觀念到熟齡慢老，實用且中立的醫學美容與健康管理文章。"
        keywords="專欄文章,逆齡觀點,曾驛翔,醫學美容文章,抗衰老知識,時光逆行駭客"
        canonicalPath="/articles"
        schema={{
          "@context": "https://schema.org",
          "@type": "Blog",
          "name": "時光逆行駭客專欄",
          "description": "曾驛翔院長的逆齡觀點與專業知識分享。",
          "author": {
            "@type": "Person",
            "name": "曾驛翔",
            "jobTitle": "院長",
          },
        }}
      />
    <div className="min-h-screen bg-abyss-900 circuit-bg">
      {/* Hero */}
      <section className="relative pt-32 md:pt-40 pb-16 md:pb-24">
        <div className="mx-auto px-4 md:px-6 lg:px-8" style={{ maxWidth: 1280 }}>
          <div className="text-center reveal">
            <span className="section-label">{articlesPageData.heroTagline}</span>
            <h1 className="mt-4 text-3xl md:text-5xl font-bold text-ghost leading-tight">
              {articlesPageData.heroTitle}
              <span className="text-gradient-gold">{articlesPageData.heroTitleAccent}</span>
            </h1>
            <p className="mt-6 text-ghost-muted text-base md:text-lg max-w-3xl mx-auto leading-relaxed">
              {articlesPageData.heroDesc}
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="pb-8 md:pb-12">
        <div className="mx-auto px-4 md:px-6 lg:px-8" style={{ maxWidth: 1280 }}>
          <div className="flex flex-wrap gap-2 md:gap-3 reveal">
            <button
              onClick={() => setActiveCategory("all")}
              className={`px-4 py-2 rounded-full text-base font-medium border transition-colors whitespace-nowrap cursor-pointer ${
                activeCategory === "all"
                  ? "bg-cyber-gold text-abyss-900 border-cyber-gold"
                  : "bg-transparent text-ghost-muted border-abyss-700 hover:border-cyber-gold/50 hover:text-ghost"
              }`}
            >
              全部文章
            </button>
            {articlesPageData.categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-4 py-2 rounded-full text-base font-medium border transition-colors whitespace-nowrap cursor-pointer ${
                  activeCategory === cat.id
                    ? "bg-cyber-gold text-abyss-900 border-cyber-gold"
                    : "bg-transparent text-ghost-muted border-abyss-700 hover:border-cyber-gold/50 hover:text-ghost"
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-8 md:py-12">
        <div className="mx-auto px-4 md:px-6 lg:px-8" style={{ maxWidth: 1280 }}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filtered.map((article, idx) => (
              <div
                key={article.id}
                className="card-cyber p-6 md:p-8 reveal flex flex-col"
                style={{ transitionDelay: `${idx * 80}ms` }}
              >
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span
                    className="px-2.5 py-1 rounded-md text-base font-mono font-medium"
                    style={{
                      color: article.accent,
                      backgroundColor: `${article.accent}15`,
                    }}
                  >
                    {article.categoryLabel}
                  </span>
                  <span className="text-ghost-dim text-base font-mono">{article.date}</span>
                </div>
                <h3 className="text-ghost font-bold text-lg md:text-xl mb-3 leading-snug">
                  {article.title}
                </h3>
                <p className="text-ghost-muted text-base leading-relaxed flex-1 mb-5">
                  {article.excerpt}
                </p>
                <Link
                  to={`/articles/${article.id}`}
                  className="inline-flex items-center gap-1.5 text-base font-medium group cursor-pointer"
                  style={{ color: article.accent }}
                >
                  閱讀更多
                  <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-16">
              <p className="text-ghost-dim text-base">此分類暫無文章</p>
            </div>
          )}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 md:py-24 border-t border-abyss-800">
        <div className="mx-auto px-4 md:px-6 lg:px-8" style={{ maxWidth: 1280 }}>
          <div className="text-center reveal">
            <span className="section-label">{articlesPageData.bottomCta.label}</span>
            <h2 className="mt-4 text-2xl md:text-4xl font-bold text-ghost">
              {articlesPageData.bottomCta.title}
            </h2>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href={lineUrl} target="_blank" rel="noopener noreferrer" className="btn-line whitespace-nowrap">
                <i className="ri-line-fill" />
                {articlesPageData.bottomCta.ctaLine}
              </a>
              <Link to="/booking" className="btn-gold whitespace-nowrap">
                {articlesPageData.bottomCta.ctaBooking}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  </>
  );
}